import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import type { GenericAbortSignal } from "axios";
import * as Sentry from "@sentry/react";
import { API_BASE_URL } from "@/config";
import { sentryIsEnabled } from "@/sentry";
import {
  axiosFingerprint,
  isAutomatedBrowser,
  shouldCaptureAxiosError,
} from "@/services/sentry-capture-policy";
import {
  classifyAxiosTransportFailure,
  normalizeApiEndpoint,
  normalizeApiMethod,
} from "@/services/sentry-api-health";
import { waitBeforeRetry } from "@/services/retry-backoff";
import { publishWorkspaceSuspended } from "@/lib/workspace-suspension-events";
import { publishApiWrite } from "@/lib/api-write-events";
import { isPageLeaving, leavePage } from "@/lib/leave-page";
import { endChatSession } from "@/lib/intercom/end-chat-session";
import { loggableRequestError } from "@/lib/errors/loggable-request-error";
import { EAuthenticationErrorCodes } from "@/types/auth";
import { ACCESS_ENDED_PATH } from "@/lib/support-access-paths";
import { TAB_ID, TAB_ID_HEADER } from "@/lib/tab-id";
import { ssoRequiredRedirect } from "@/lib/sso-required-path";

/** The code the API answers a Cognota support identity with once its temporary access has ended
 *  (NG-3417, Phase 2 D5). */
const SUPPORT_ACCESS_ENDED_CODE = "support_access_ended";

/** Survives a full page load, but lets a later visit refresh the workspace list. */
const SUSPENDED_RELOAD_WINDOW_MS = 30_000;

function shouldReloadSuspendedWorkspace(slug: string): boolean {
  const landing = `/${slug}`;
  try {
    const key = `workspace-suspended-reload:${slug}`;
    const previous = window.sessionStorage.getItem(key);
    const now = Date.now();
    if (previous !== null && now - Number(previous) < SUSPENDED_RELOAD_WINDOW_MS) return false;
    // Record before navigating, including when the list still says "active".
    window.sessionStorage.setItem(key, String(now));
    return true;
  } catch (error) {
    console.warn("[api] suspended workspace reload storage unavailable", { slug }, error);
    return window.location.pathname.replace(/\/$/, "") !== landing;
  }
}

/** Verbs that change server state, i.e. that can invalidate a mounted view's data. */
const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

/**
 * Attempts (1 initial + retries) for an idempotent GET that hits a transient
 * connection reset. The backend resets keep-alive connections under load, which
 * a bare GET surfaces as a non-recovering "Network Error" in the route/store
 * loaders (a settings tab renders an error card, a list renders empty). Retrying
 * the reset lets the loader recover on its own instead. (#1886)
 */
export const TRANSIENT_GET_ATTEMPTS = 3;

/** Backoff before retry N of a transient GET (multiplied by the attempt number). */
const TRANSIENT_GET_BACKOFF_MS = 150;

/**
 * Per-request timeout (ms) for idempotent GETs. Axios defaults to no timeout, so
 * a backend that accepts the connection but never answers (saturated, holding the
 * socket open — not a reset) left a loader's `isLoading` true forever: the UI sat
 * in an indefinite skeleton with no recovery, and a real user had to hard-reload
 * the page (the #1929 workflow-tab flake was the test-visible symptom). Bounding
 * the wait turns a hang into a surfaced error so the loader can render its
 * recoverable error state with a retry. GET-only: a timed-out mutation may have
 * applied server-side, so aborting/retrying it isn't safe (see `withTransientRetry`).
 */
export const GET_TIMEOUT_MS = 30_000;

/**
 * A network-level failure with no HTTP response (connection reset / DNS) —
 * transient under load and safe to retry for an idempotent request. A request
 * that received an HTTP response (4xx/5xx) is a real result, not transient; an
 * intentionally-canceled request (latest-wins guards, unmount) must not be retried;
 * and a client-side timeout (`ECONNABORTED`) already waited `GET_TIMEOUT_MS`, so
 * retrying it just pins the skeleton N× longer — surface it instead so the loader
 * shows a recoverable error and the user can retry.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (axios.isCancel(error)) return false;
  const e = error as AxiosError;
  if (e?.code === "ECONNABORTED" || e?.code === "ETIMEDOUT") return false;
  return Boolean(e?.isAxiosError) && !e.response;
}

/**
 * Headers every API request carries. `X-Tab-Id` names this page load, so a
 * cache hint caused by this tab's own write comes back naming it (NG-3587).
 * Only API requests carry it: every request here goes to `API_BASE_URL`, whose
 * CORS allows it, while uploads to storage use a bare `axios`.
 */
const API_HEADERS = { "Content-Type": "application/json", [TAB_ID_HEADER]: TAB_ID };

/** Shared Axios instance — created once, reused by all services. */
const sharedAxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: API_HEADERS,
});

function recordAxiosTransportFailure(error: unknown): void {
  if (!sentryIsEnabled() || isAutomatedBrowser()) return;
  const outcome = classifyAxiosTransportFailure(error);
  const axiosError = error as AxiosError;
  const endpoint = normalizeApiEndpoint(axiosError.config?.url, API_BASE_URL);
  if (!outcome || !endpoint) return;

  const method = normalizeApiMethod(axiosError.config?.method);
  const span = Sentry.startInactiveSpan({
    name: `${method} ${endpoint}`,
    op: "api.client.failure",
    onlyIfParent: true,
    attributes: {
      "api.endpoint": endpoint,
      "api.method": method,
      "api.outcome": outcome,
    },
  });
  span.setStatus({
    code: 2,
    message: outcome === "timeout" ? "deadline_exceeded" : "internal_error",
  });
  span.end();
}

sharedAxiosInstance.interceptors.response.use(
  (response) => {
    // Tell mounted views that server state changed, so a long-lived view can
    // re-read instead of rendering what it loaded on arrival (#3712). Only the
    // verb + url travel; interpreting them is the subscriber's job.
    const method = response.config?.method?.toLowerCase();
    if (method && MUTATING_METHODS.has(method)) {
      publishApiWrite({ method, url: response.config?.url ?? "" });
    }
    return response;
  },
  async (error) => {
    // The page is being replaced: Safari fails every request while a full page
    // load is pending, and a caller reacting to that failure by navigating
    // in-app would abandon the load (NG-2513). Report nothing and never settle.
    if (isPageLeaving()) return new Promise<never>(() => {});
    // A logged request error goes to Sentry Logs whole, so it serialises with
    // what was sent and what the server answered, minus the headers and any
    // secret-named field (NG-2189). Set first: this handler logs it too.
    if (axios.isAxiosError(error)) {
      Object.defineProperty(error, "toJSON", {
        value: () => loggableRequestError(error),
        configurable: true,
        writable: true,
      });
    }
    recordAxiosTransportFailure(error);
    const status = error.response?.status;
    const errorCode = error.response?.data?.error_code;
    if (status === 403 && error.response?.data?.code === "workspace_suspended") {
      console.warn("[api] workspace suspended", {
        status,
        code: error.response.data.code,
        url: error.config?.url,
        method: error.config?.method,
      });
      // During navigation the address bar can still name the workspace being
      // left. Prefer the refused request's slug. ID-keyed file assets have no
      // slug in their URL, so those use the current product route.
      const requestSlug = error.config?.url?.match(
        /\/api\/workspaces\/(?!file-assets\/)([^/?#]+)/,
      )?.[1];
      const workspaceSlug = requestSlug ?? window.location.pathname.split("/")[1];
      publishWorkspaceSuspended(workspaceSlug);
      // The wrapper can now show the panel even if the refreshed list was stale.
      // Only the first refusal reloads; later callers receive their original error.
      if (!shouldReloadSuspendedWorkspace(workspaceSlug)) return Promise.reject(error);
      return leavePage(`/${workspaceSlug}`);
    }

    // NG-2471: a session that is simply gone reads as anonymous, and the API
    // answers a session caller 403, not 401, marked with this code. It is the
    // same news as a 401, so it routes to sign-in the same way. Keyed on the
    // code, never the message text: a 403 without it is a permission denial
    // and stays with the page.
    // NG-3417, D9: a support identity's access ended (left, ended from the Backoffice, expired, or
    // the kill switch). Every page shows "Access ended" rather than a stray permission error.
    // Only a support identity is ever answered with this code. Never settles, as below.
    if (
      status === 403 &&
      errorCode === SUPPORT_ACCESS_ENDED_CODE &&
      window.location.pathname !== ACCESS_ENDED_PATH
    ) {
      return leavePage(ACCESS_ENDED_PATH);
    }
    // NG-3331: the workspace on screen wants its single sign-on, which this
    // session's sign-in doesn't hold. The person goes to the page naming the
    // identity providers they still need. Only the API sends this code, and only
    // with sso-shared-identity-providers on. Never settles, as below.
    const ssoRequired = ssoRequiredRedirect(status, error.response?.data, window.location.pathname);
    if (ssoRequired) return leavePage(ssoRequired);
    const sessionGone = status === 403 && errorCode === EAuthenticationErrorCodes.NOT_AUTHENTICATED;
    if ((status === 401 || sessionGone) && window.location.pathname !== "/") {
      const nextPath = window.location.pathname;
      const params = new URLSearchParams({ next_path: nextPath });
      // NG-1762, FR51: SessionTimeoutMiddleware's 401 body carries a
      // distinct error_code (unlike DRF's default unauthenticated response,
      // which has none) — plumbed through here, and rendered as the "session
      // expired" banner at the render layer (authErrorHandler). The gone-session
      // 403's code is not forwarded: it is the silent redirect the auth
      // guard has always made for an absent session, and has no banner.
      if (status === 401 && typeof errorCode === "string") params.set("error_code", errorCode);
      // The NextGen session is over, so the Intercom one ends with it (NG-3452,
      // AC8). Keep in step with `AuthService.signOut`.
      endChatSession();
      // Never settles, so no caller reacts (a loader redirecting in-app would
      // make Safari abandon this load and loop, NG-2513).
      return leavePage(`/?${params.toString()}`);
    }

    if (sentryIsEnabled()) {
      const context = {
        method: error.config?.method,
        url: error.config?.url,
        status: status ?? null,
      };
      if (shouldCaptureAxiosError(error)) {
        // A real fault (5xx / network / timeout). Capture with a per-endpoint
        // fingerprint so one broken endpoint = one Sentry issue, instead of
        // every AxiosError merging on the shared settle.js throw frame (#2015).
        Sentry.captureException(error, {
          fingerprint: axiosFingerprint(error),
          extra: context,
        });
      } else {
        // A handled/expected result (4xx / canceled request / a no-response
        // transport failure on a safe request — an idempotent GET or a
        // read-shaped POST). Don't raise a Sentry error — leave a breadcrumb so
        // it's still visible as context on the next captured event, and nothing
        // is silently swallowed (#2015).
        if (status === undefined && !axios.isCancel(error)) {
          // A dropped transport failure still leaves a warn-level trail in
          // Sentry logs, so a systemic outage stays visible by volume (#2756).
          console.warn("[api] no-response failure on a safe request (transport)", context, error);
        }
        Sentry.addBreadcrumb({
          category: "http.handled",
          type: "http",
          level: "info",
          message: `${context.method ?? "?"} ${context.url ?? "?"} → ${context.status ?? "network error"}`,
          data: context,
        });
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Axios config plus the options `ApiService` reads itself and strips before the
 * request goes out.
 */
export interface ApiRequestConfig extends AxiosRequestConfig {
  /**
   * `false` turns the transient-GET retry off for this call because its caller
   * owns retry. A TanStack Query read does (`app/queries/policies.ts`): stacking
   * the query's attempts on these three would multiply transport attempts, so a
   * query-owned request has exactly one retry owner (NG-1313). Defaults to on.
   */
  transientRetry?: boolean;
}

/** The shared `BasePaginator` cursor envelope, generic over the row type. */
interface CursorPage<T> {
  results: T[];
  next_cursor: string;
  next_page_results: boolean;
}

export abstract class ApiService {
  protected baseURL: string;
  protected axiosInstance: AxiosInstance;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.axiosInstance = sharedAxiosInstance;
  }

  /**
   * Run an idempotent request, retrying a transient connection reset (no HTTP
   * response received) a few times before giving up. Only used for GETs —
   * retrying a mutation on a reset could double-apply it. (#1886)
   *
   * The backoff between attempts is cancellable (`waitBeforeRetry`): a caller
   * that aborted mid-sleep gets its error now instead of after a retry nobody
   * is waiting for, and a retry scheduled before the host environment went away
   * never wakes up at all (#4200).
   */
  private async withTransientRetry<T>(
    run: () => Promise<T>,
    signal?: GenericAbortSignal,
  ): Promise<T> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= TRANSIENT_GET_ATTEMPTS; attempt++) {
      try {
        return await run();
      } catch (error) {
        if (!isTransientNetworkError(error) || attempt === TRANSIENT_GET_ATTEMPTS) throw error;
        lastError = error;
        console.warn(
          `[api] transient network error (attempt ${attempt}/${TRANSIENT_GET_ATTEMPTS}); retrying`,
          error,
        );
        if (!(await waitBeforeRetry(TRANSIENT_GET_BACKOFF_MS * attempt, signal))) throw error;
      }
    }
    throw lastError;
  }

  protected get<T>(url: string, config?: ApiRequestConfig): Promise<T> {
    const { transientRetry = true, ...axiosConfig } = config ?? {};
    const run = (): Promise<T> =>
      // Bound the wait so a hung response can't pin a loader in a permanent
      // loading state (GET_TIMEOUT_MS); a caller may override via `config.timeout`.
      this.axiosInstance
        .get(url, { timeout: GET_TIMEOUT_MS, ...axiosConfig })
        .then((res) => res.data);
    return transientRetry ? this.withTransientRetry(run, axiosConfig.signal) : run();
  }

  protected post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.post(url, data, config).then((res) => res.data);
  }

  protected patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.patch(url, data, config).then((res) => res.data);
  }

  protected put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.put(url, data, config).then((res) => res.data);
  }

  protected delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.axiosInstance.delete(url, config).then((res) => res.data);
  }

  /**
   * POST that returns the raw `Response` instead of parsed JSON. Used by the
   * CSV export, whose endpoint answers either `200 text/csv` (stream the file)
   * or `202 application/json` (async; poll the token) — the caller branches on
   * status/content-type. Uses `fetch` (not the Axios instance) so the body is
   * never eagerly parsed; credentials and headers mirror the Axios instance's.
   */
  protected async postRaw(url: string, data?: unknown): Promise<Response> {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: "POST",
      credentials: "include",
      headers: API_HEADERS,
      body: data === undefined ? undefined : JSON.stringify(data),
    });
    await this.leaveIfSsoRequired(response);
    // These two bypass the Axios instance, so they'd also bypass its
    // write-announcing interceptor — publish here to keep the signal complete.
    if (response.ok) publishApiWrite({ method: "post", url });
    return response;
  }

  /**
   * DELETE that returns the raw `Response` (not parsed JSON), so the caller can
   * branch on status — e.g. the workspace bulk-delete answers `200 {deleted,
   * skipped}` for a small selection or `202 {token}` when it hands off to a
   * background job. Mirrors {@link postRaw}.
   */
  protected async deleteRaw(url: string, data?: unknown): Promise<Response> {
    const response = await fetch(`${this.baseURL}${url}`, {
      method: "DELETE",
      credentials: "include",
      headers: API_HEADERS,
      body: data === undefined ? undefined : JSON.stringify(data),
    });
    await this.leaveIfSsoRequired(response);
    if (response.ok) publishApiWrite({ method: "delete", url });
    return response;
  }

  /**
   * The raw calls skip the Axios interceptor, so they send a refusal of the
   * workspace on screen (403 `sso_sign_in_required`, NG-3901) to the SSO page
   * themselves, as the interceptor does. Never settles when it leaves.
   */
  private async leaveIfSsoRequired(response: Response): Promise<void> {
    if (response.status !== 403) return;
    const body: unknown = await response
      .clone()
      .json()
      .catch((error: unknown) => {
        console.warn("[api] raw 403 body unreadable", { status: response.status }, error);
        return null;
      });
    const ssoRequired = ssoRequiredRedirect(response.status, body, window.location.pathname);
    if (ssoRequired) await leavePage(ssoRequired);
  }

  /**
   * Walk a cursor-paginated endpoint (the shared `BasePaginator` envelope) and
   * flatten every page into one list. The server still paginates each request
   * (responses stay bounded); this assembles the pages client-side for callers
   * that need the full scoped set — e.g. a per-team / per-program project table
   * that filters and sorts locally. `buildUrl` owns the endpoint path + any
   * filters; the helper supplies `per_page` and `cursor`. `config` goes on every
   * page's request, so a query's abort signal stops the walk.
   */
  protected async getAllPages<T>(
    buildUrl: (args: { cursor: string | null; perPage: number }) => string,
    perPage = 200,
    config?: ApiRequestConfig,
  ): Promise<T[]> {
    const all: T[] = [];
    let cursor: string | null = null;
    while (true) {
      const page: CursorPage<T> = await this.get<CursorPage<T>>(
        buildUrl({ cursor, perPage }),
        config,
      );
      all.push(...page.results);
      // Stop at the last page, or if the server fails to advance the cursor.
      if (!page.next_page_results || !page.next_cursor || page.next_cursor === cursor) break;
      cursor = page.next_cursor;
    }
    return all;
  }
}
